import { useCallback, useEffect, useState } from "react";
import { ENITEO_APP_ID, ENITEO_PROVIDER_ID } from "@forgepoint/shared";
import { Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { useConnectionsStore } from "@/store/connectionsStore";
import { toast } from "sonner";

const DEFAULT_PORT = "1433";

interface EniteoConnectionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: (connectionId: string) => void;
}

/**
 * Dialog collecting the Eniteo SQL Server endpoint and service-account
 * credentials, plus an optional separate write database and an optional
 * separate write login.
 * Eniteo's named instance cannot be resolved over the static egress tunnel,
 * so the fixed TCP port is always entered explicitly.
 * @param props - EniteoConnectionDialogProps
 * @returns Connection dialog element
 */
export function EniteoConnectionDialog({
  open,
  onOpenChange,
  onSuccess,
}: EniteoConnectionDialogProps) {
  const [server, setServer] = useState("");
  const [port, setPort] = useState(DEFAULT_PORT);
  const [database, setDatabase] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [encrypt, setEncrypt] = useState(true);
  const [trustServerCertificate, setTrustServerCertificate] = useState(false);
  const [requiresEgressProxy, setRequiresEgressProxy] = useState(true);
  const [hasWriteDatabase, setHasWriteDatabase] = useState(false);
  const [writeServer, setWriteServer] = useState("");
  const [writePort, setWritePort] = useState(DEFAULT_PORT);
  const [writeDatabase, setWriteDatabase] = useState("");
  const [hasWriteLogin, setHasWriteLogin] = useState(false);
  const [writeUsername, setWriteUsername] = useState("");
  const [writePassword, setWritePassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const finalizeConnection = useConnectionsStore(
    (state) => state.finalizeConnection,
  );
  const fetchConnectionsByApp = useConnectionsStore(
    (state) => state.fetchConnectionsByApp,
  );

  useEffect(() => {
    if (!open) {
      setServer("");
      setPort(DEFAULT_PORT);
      setDatabase("");
      setUsername("");
      setPassword("");
      setEncrypt(true);
      setTrustServerCertificate(false);
      setRequiresEgressProxy(true);
      setHasWriteDatabase(false);
      setHasWriteLogin(false);
      setWriteServer("");
      setWritePort(DEFAULT_PORT);
      setWriteDatabase("");
      setWriteUsername("");
      setWritePassword("");
      setError(null);
      setIsSubmitting(false);
    }
  }, [open]);

  const handleSubmit = useCallback(async () => {
    const trimmedServer = server.trim();
    const trimmedDatabase = database.trim();
    const trimmedUsername = username.trim();
    const parsedPort = Number(port);
    if (!trimmedServer || !trimmedDatabase) {
      setError("Provide the Eniteo server and database.");
      return;
    }
    if (!isValidPort(parsedPort)) {
      setError("Provide the fixed TCP port the Eniteo instance listens on.");
      return;
    }
    if (!trimmedUsername || !password) {
      setError("Provide the Eniteo username and password.");
      return;
    }
    const trimmedWriteServer = writeServer.trim();
    const trimmedWriteDatabase = writeDatabase.trim();
    const parsedWritePort = Number(writePort);
    if (hasWriteDatabase && (!trimmedWriteServer || !trimmedWriteDatabase)) {
      setError("Provide the write database server and database.");
      return;
    }
    if (hasWriteDatabase && !isValidPort(parsedWritePort)) {
      setError("Provide the fixed TCP port the write database listens on.");
      return;
    }
    const trimmedWriteUsername = writeUsername.trim();
    if (hasWriteLogin && (!trimmedWriteUsername || !writePassword)) {
      setError("Provide the write username and password.");
      return;
    }
    setError(null);
    setIsSubmitting(true);
    try {
      const connection = await finalizeConnection({
        connectionProviderId: ENITEO_PROVIDER_ID,
        appId: ENITEO_APP_ID,
        credentials: {
          type: "EniteoServiceCredentials",
          username: trimmedUsername,
          password,
          ...(hasWriteLogin && {
            write: {
              username: trimmedWriteUsername,
              password: writePassword,
            },
          }),
        },
        config: {
          server: trimmedServer,
          port: parsedPort,
          database: trimmedDatabase,
          encrypt,
          trustServerCertificate,
          ...(hasWriteDatabase && {
            write: {
              server: trimmedWriteServer,
              port: parsedWritePort,
              database: trimmedWriteDatabase,
            },
          }),
        },
        requiresEgressProxy,
        name: `Eniteo (${trimmedDatabase})`,
      });
      if (!connection) {
        const finalizeError = useConnectionsStore
          .getState()
          .connectionStates.get(ENITEO_APP_ID)?.finalizeError;
        setError(
          finalizeError ||
            "Eniteo connection validation failed. Check the endpoint and credentials.",
        );
        return;
      }
      toast.success("Eniteo connection created successfully.");
      await fetchConnectionsByApp(ENITEO_APP_ID);
      onSuccess?.(connection.id);
      onOpenChange(false);
    } catch (submitError) {
      setError(
        submitError instanceof Error
          ? submitError.message
          : "Failed to create Eniteo connection.",
      );
    } finally {
      setIsSubmitting(false);
    }
  }, [
    server,
    port,
    database,
    username,
    password,
    encrypt,
    trustServerCertificate,
    requiresEgressProxy,
    hasWriteDatabase,
    hasWriteLogin,
    writeServer,
    writePort,
    writeDatabase,
    writeUsername,
    writePassword,
    fetchConnectionsByApp,
    finalizeConnection,
    onOpenChange,
    onSuccess,
  ]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Connect Eniteo</DialogTitle>
          <DialogDescription>
            Enter your Eniteo database connection details.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid gap-4 sm:grid-cols-[1fr_8rem]">
            <div className="grid gap-2">
              <Label htmlFor="eniteo-server">Server</Label>
              <Input
                id="eniteo-server"
                value={server}
                onChange={(event) => setServer(event.target.value)}
                placeholder="sql5.example.com"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="eniteo-port">Port</Label>
              <Input
                id="eniteo-port"
                type="number"
                inputMode="numeric"
                value={port}
                onChange={(event) => setPort(event.target.value)}
              />
            </div>
          </div>
          <div className="grid gap-2">
            <Label htmlFor="eniteo-database">Database</Label>
            <Input
              id="eniteo-database"
              value={database}
              onChange={(event) => setDatabase(event.target.value)}
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="eniteo-username">Username</Label>
            <Input
              id="eniteo-username"
              value={username}
              onChange={(event) => setUsername(event.target.value)}
              autoComplete="username"
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="eniteo-password">Password</Label>
            <Input
              id="eniteo-password"
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              autoComplete="current-password"
            />
          </div>
          <div className="flex items-center justify-between gap-4">
            <div className="grid gap-1">
              <Label htmlFor="eniteo-encrypt">Encrypt connection</Label>
              <p className="text-sm text-muted-foreground">
                Negotiate TLS with the SQL Server before authenticating.
              </p>
            </div>
            <Switch
              id="eniteo-encrypt"
              checked={encrypt}
              onCheckedChange={setEncrypt}
            />
          </div>
          {encrypt && (
            <div className="flex items-center justify-between gap-4">
              <div className="grid gap-1">
                <Label htmlFor="eniteo-trust-certificate">
                  Trust server certificate
                </Label>
                <p className="text-sm text-muted-foreground">
                  Accept a self-signed certificate from the SQL Server.
                </p>
              </div>
              <Switch
                id="eniteo-trust-certificate"
                checked={trustServerCertificate}
                onCheckedChange={setTrustServerCertificate}
              />
            </div>
          )}
          <div className="flex items-center justify-between gap-4">
            <div className="grid gap-1">
              <Label htmlFor="eniteo-egress-proxy">
                Require static egress IP
              </Label>
              <p className="text-sm text-muted-foreground">
                Route this connection through Forgepoint's allowlistable IP.
              </p>
            </div>
            <Switch
              id="eniteo-egress-proxy"
              checked={requiresEgressProxy}
              onCheckedChange={setRequiresEgressProxy}
            />
          </div>
          <div className="flex items-center justify-between gap-4">
            <div className="grid gap-1">
              <Label htmlFor="eniteo-write-database">
                Separate write database
              </Label>
              <p className="text-sm text-muted-foreground">
                Only needed when quotes must be written to a different database
                than the one Forgepoint reads from, such as a read replica.
              </p>
            </div>
            <Switch
              id="eniteo-write-database"
              checked={hasWriteDatabase}
              onCheckedChange={setHasWriteDatabase}
            />
          </div>
          {hasWriteDatabase && (
            <>
              <div className="grid gap-4 sm:grid-cols-[1fr_8rem]">
                <div className="grid gap-2">
                  <Label htmlFor="eniteo-write-server">Write server</Label>
                  <Input
                    id="eniteo-write-server"
                    value={writeServer}
                    onChange={(event) => setWriteServer(event.target.value)}
                    placeholder="sql6.example.com"
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="eniteo-write-port">Write port</Label>
                  <Input
                    id="eniteo-write-port"
                    type="number"
                    inputMode="numeric"
                    value={writePort}
                    onChange={(event) => setWritePort(event.target.value)}
                  />
                </div>
              </div>
              <div className="grid gap-2">
                <Label htmlFor="eniteo-write-database-name">
                  Write database
                </Label>
                <Input
                  id="eniteo-write-database-name"
                  value={writeDatabase}
                  onChange={(event) => setWriteDatabase(event.target.value)}
                />
              </div>
            </>
          )}
          <div className="flex items-center justify-between gap-4">
            <div className="grid gap-1">
              <Label htmlFor="eniteo-write-login">Separate write login</Label>
              <p className="text-sm text-muted-foreground">
                Only needed when quote pushes must sign in with a different SQL
                login than the one Forgepoint reads with.
              </p>
            </div>
            <Switch
              id="eniteo-write-login"
              checked={hasWriteLogin}
              onCheckedChange={setHasWriteLogin}
            />
          </div>
          {hasWriteLogin && (
            <>
              <div className="grid gap-2">
                <Label htmlFor="eniteo-write-username">Write username</Label>
                <Input
                  id="eniteo-write-username"
                  value={writeUsername}
                  onChange={(event) => setWriteUsername(event.target.value)}
                  autoComplete="off"
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="eniteo-write-password">Write password</Label>
                <Input
                  id="eniteo-write-password"
                  type="password"
                  value={writePassword}
                  onChange={(event) => setWritePassword(event.target.value)}
                  autoComplete="new-password"
                />
              </div>
            </>
          )}
          {error && <p className="text-sm text-red-500">{error}</p>}
        </div>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button type="button" onClick={handleSubmit} disabled={isSubmitting}>
            Connect
            {isSubmitting && (
              <Loader2 className="ml-2 h-3.5 w-3.5 animate-spin" />
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Checks a parsed port is a valid TCP port number.
 * @param port - The parsed port
 * @returns Whether the port is an integer from 1 to 65535
 */
function isValidPort(port: number): boolean {
  return Number.isInteger(port) && port >= 1 && port <= 65535;
}
